import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ErrorBoundary global - capture les erreurs React et affiche un fallback
 * propre au lieu d'un ecran blanc.
 *
 * Sans ErrorBoundary, une simple erreur dans un composant rend toute l'app
 * blanche (cause #1 des "ecrans blancs" en prod).
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log to console for debugging
    console.error('ErrorBoundary caught:', error, errorInfo);
    // Optional: send to backend logging service
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || ''),
          componentStack: String(errorInfo?.componentStack || ''),
          path: window.location.pathname,
          ts: new Date().toISOString(),
        }),
      }).catch(() => { /* silent */ });
    } catch (e) { /* silent */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1B3A4B] to-[#0d2937] p-4"
           data-testid="error-boundary-fallback">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
          <div className="bg-red-50 border-b-4 border-red-500 p-6 text-center">
            <AlertTriangle className="w-14 h-14 text-red-600 mx-auto mb-2" />
            <h1 className="text-2xl font-bold text-red-800">Une erreur est survenue</h1>
            <p className="text-sm text-red-700 mt-2">
              L'application a rencontre un probleme inattendu. Vous pouvez reessayer ou recharger la page.
            </p>
          </div>
          <div className="p-6">
            <details className="mb-4 text-xs text-gray-500">
              <summary className="cursor-pointer text-[#1B3A4B] font-semibold">
                Details techniques (pour le support)
              </summary>
              <pre className="mt-2 p-2 bg-gray-50 rounded overflow-auto max-h-40">
                {String(this.state.error?.message || this.state.error)}
                {'\n\n'}
                {String(this.state.error?.stack || '').slice(0, 500)}
              </pre>
            </details>
            <div className="flex gap-2 flex-col sm:flex-row">
              <button
                onClick={this.handleReset}
                data-testid="error-retry-btn"
                className="flex-1 bg-[#1B3A4B] hover:bg-[#0d2937] text-white px-4 py-2 rounded font-semibold flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" /> Reessayer
              </button>
              <button
                onClick={this.handleReload}
                data-testid="error-reload-btn"
                className="flex-1 bg-[#D4A745] hover:bg-[#b88a30] text-white px-4 py-2 rounded font-semibold"
              >
                Recharger la page
              </button>
            </div>
            <p className="text-xs text-center text-gray-400 mt-4">
              Si le probleme persiste, contactez le support : <a href="mailto:contact@gabservices.ml" className="underline">contact@gabservices.ml</a>
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
